import { useMaestroStore } from '../store/maestroStore';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { TransportState } from './TransportState';
import { isReconcileDegraded } from './backgroundTransportReconcile';
import { clearGaplessPrefetchIndex, clearSetNextFillConfirmed, getAdvanceGeneration } from './remoteAdvanceControl';

export const GAPLESS_HANDOFF_VERIFY_DELAY_MS = 1500;

export function parseRendererTransportState(rawResp: string): string {
  const match = rawResp.match(/<CurrentTransportState>(.*?)<\/CurrentTransportState>/);
  return match?.[1] || 'UNKNOWN';
}

/**
 * #11309: timer-based SetNext renderers adopt the next queue track optimistically at the
 * boundary. A renderer that is not PLAYING/TRANSITIONING a moment later never received the
 * next URI — its current URI is still the OLD track, so a bare Play would replay it. Hand
 * the adopted track to the queue's play path instead.
 */
export async function verifyGaplessHandoffPlayback(
  capturedGen: number,
  queueIndex: number,
  playTrack: (track: unknown, options: { intent: 'queue-advance' }) => Promise<void>,
): Promise<void> {
  if (capturedGen !== getAdvanceGeneration()) return;

  const store = useMaestroStore.getState();
  if (store.queueIndex !== queueIndex) return;
  // #11452: the device's STOPPED report reaches the store inside this window; only a user pause cancels.
  if (store.playbackState !== 'playing' && store.playbackState !== 'stopped') return;
  if (getSyncHandler().isObserving()) return;
  if (isReconcileDegraded()) return;

  const renderer = store.activeConnectorRenderer;
  if (!renderer?.id) return;

  try {
    const C = await import('../lib/connector');
    const raw = await C.getTransportInfoBackground(renderer.id);
    const rendererState = parseRendererTransportState(raw);

    if (rendererState === 'PLAYING' || rendererState === 'TRANSITIONING') {
      console.log('[QueueController] gapless handoff verify — renderer playing OK');
      return;
    }

    const { queue, shuffled, shuffleOrder } = store;
    const actualIdx = shuffled && shuffleOrder.length > queueIndex
      ? shuffleOrder[queueIndex]
      : queueIndex;
    const track = queue[actualIdx]?.track;

    console.warn(
      '[QueueController] gapless handoff verify — renderer',
      rendererState,
      '— playing adopted track via playUri (no bare Play)',
    );
    diagnosticLog('warn', 'playback', 'Gapless handoff failed — playUri adopted track', {
      rendererState, storeState: store.playbackState, queueIndex, trackId: track?.id,
    });
    clearGaplessPrefetchIndex();
    clearSetNextFillConfirmed();

    if (track) {
      await playTrack(track, { intent: 'queue-advance' });
    }
  } catch (err) {
    const C = await import('../lib/connector');
    if (C.isLocalTiersExhaustedError(err)) {
      console.debug('[QueueController] gapless handoff verify halted — local tiers exhausted');
      TransportState.enterBackgroundReconcileDegraded('gapless-handoff-verify');
      return;
    }
    const message = err instanceof Error ? err.message : String(err);
    console.warn('[QueueController] gapless handoff verify failed:', message);
  }
}
